import * as React from 'react';
import { Subheading, Paragraph, Heading } from 'components/src/typography';
import Button from 'components/src/button';
import Label from 'components/src/label';
import { Cards2f1FieldsFragment } from 'types/generated';
import { getOr } from '@/utils/options';
import Image from 'next/image';
import { useTwoForOneContext } from './TwoForOneContext';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document, BLOCKS } from '@contentful/rich-text-types';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

type Props = {
  data: Cards2f1FieldsFragment;
};

const getActiveDays = (activeDays: Array<string | null>) => {
  const days = ['Mán', 'Þri', 'Mið', 'Fim', 'Fös', 'Lau', 'Sun'];
  return (
    <span className="flex select-none flex-wrap gap-8">
      {days.map((day) => (
        <Label
          key={day}
          label={day}
          type="pill"
          colorVariant={activeDays.some((activeDay) => activeDay === day) ? 'lightBlue' : 'disabled'}
        />
      ))}
    </span>
  );
};

const Card = ({ data }: Props) => {
  const { setActiveCard } = useTwoForOneContext();
  const [isHighlighted, setIsHighlighted] = useState(false);
  const { asPath, replace } = useRouter();
  const ref = useRef<HTMLDivElement | null>(null);
  const pathParts = (asPath as string).split('#');

  useEffect(() => {
    if (pathParts.length > 1) {
      const fragmentId = decodeURIComponent(pathParts[1]);
      if (data.id === fragmentId && ref.current) {
        replace(pathParts[0], undefined, { shallow: true }).catch((e) => {
          if (!e.cancelled) {
            throw e;
          }
        });
        ref.current.scrollIntoView();
        setTimeout(() => {
          setIsHighlighted(true);
        }, 100);
        setTimeout(() => {
          setIsHighlighted(false);
        }, 2000);
      }
    }
  }, [data, pathParts, replace, setActiveCard, setIsHighlighted, ref]);

  return (
    <div
      ref={ref}
      className={`col-span-full flex min-h-[388px] flex-col justify-between rounded-40 border-1 bg-white-100 transition duration-500
       ease-out sm:col-span-3 md:col-span-4
       ${isHighlighted ? 'border-primary-red shadow-lg' : 'border-black-25'}`}
    >
      <div>
        <div className="relative h-[160px]">
          {data.image?.url && (
            <Image
              src={data.image.url}
              alt=""
              fill
              sizes={'750px'}
              className="h-auto w-full rounded-t-40 object-cover"
            />
          )}
        </div>
        <div className="flex flex-col gap-medium p-lg">
          <Heading variant="medium" classes="text-black-50">
            {getOr(data.companyName, '')}
          </Heading>
          <Subheading classes="title-lg text-black-50">{getOr(data.title, '')}</Subheading>
          {data.description?.json &&
            documentToReactComponents(data.description.json as Document, {
              renderNode: {
                [BLOCKS.PARAGRAPH]: (_node, children) => {
                  return (
                    <Paragraph variant="small" classes="body-md text-black-50 empty:h-16">
                      {children}
                    </Paragraph>
                  );
                },
              },
            })}
        </div>
      </div>
      <div className="flex flex-col gap-medium p-lg">
        {data.address && (
          <div className="flex gap-x-16">
            <Paragraph variant="small" classes="my-auto text-black-50">
              {data.address}
            </Paragraph>
          </div>
        )}
        {data.time && (
          <div className="flex gap-x-16">
            <Paragraph variant="small" classes="my-auto select-none text-black-50">
              {data.time}
            </Paragraph>
          </div>
        )}
        <div className="flex gap-8">{data.days && getActiveDays(data.days)}</div>
        <Button
          label="Virkja tilboð"
          variant="primary"
          autoWidth={false}
          classes="mt-16"
          labelClasses="!font-medium"
          onClick={() => setActiveCard(data)}
        />
      </div>
    </div>
  );
};

export default Card;
